import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MainLayout from './MainLayout';
import { fetchProductSuggestions } from '../../redux/slices/productSlice';
import Apiurl from '../../Helpers/apiUrl';
import { useQuery, useMutation } from '@tanstack/react-query';
import Swal from 'sweetalert2';

import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TablePagination, TextField, InputAdornment, Button, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
    Typography, CircularProgress, Tooltip,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ClickableTableLink } from './ClickableTableLink';
import { TableBodySkeleton } from "../utils/TableSkeleton";

async function updateSuggestionsApi({ id, suggestedSkus }) {
    const token = localStorage.getItem('u_token');
    const res = await fetch(`${Apiurl}admin/update-suggestion`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ id, suggestedSkus }),
    });
    if (!res.ok) throw new Error('Failed to update suggestions');
    return res.json();
}

const MODAL_PAGE_SIZE = 30;

function useFilteredProducts(masterProduct, searchTerm, excludeSku) {
    return useQuery({
        queryKey: ['modal-products', searchTerm, excludeSku],
        queryFn: () => {
            const lower = searchTerm.toLowerCase();
            return (masterProduct || []).filter(p =>
                p.sku_code !== excludeSku &&
                (!lower ||
                    p.sku_code?.toLowerCase().includes(lower) ||
                    p.product_desc?.toString().toLowerCase().includes(lower) ||
                    p.brand?.toLowerCase().includes(lower) ||
                    p.category?.toLowerCase().includes(lower))
            );
        },
        placeholderData: prev => prev,
        staleTime: 60_000,
    });
}

function ProductItem({ product, isSelected, isDisabled, onToggle }) {
    const { sku_code, product_desc, brand, category, image } = product;

    return (
        <Box
            onClick={() => !isDisabled && onToggle(sku_code)}
            sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, mb: 0.5, borderRadius: '8px',
                border: '1px solid', borderColor: isSelected ? '#90caf9' : '#e0e6ed',
                bgcolor: isSelected ? '#e3f2fd' : '#fafbfc',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.45 : 1,
                transition: 'all 0.15s',
                '&:hover': !isDisabled ? { borderColor: '#0288d1', bgcolor: isSelected ? '#e3f2fd' : '#f0f7ff' } : {},
            }}
        >
            {image ? (
                <img
                    src={image} alt={sku_code}
                    style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover', border: '1px solid #e0e6ed', flexShrink: 0 }}
                    onError={e => { e.target.style.display = 'none'; }}
                />
            ) : (
                <Box sx={{
                    width: 36, height: 36, borderRadius: '6px', border: '1px solid #e0e6ed',
                    bgcolor: '#f0f4f8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    <InventoryOutlinedIcon sx={{ fontSize: 16, color: '#b0bec5' }} />
                </Box>
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#263238', lineHeight: 1.3 }} noWrap>
                    {product_desc || sku_code}
                </Typography>
                <Typography variant="caption" sx={{ color: '#90a4ae', fontFamily: 'monospace' }}>
                    {sku_code}{brand ? ` · ${brand}` : ''}{category ? ` · ${category}` : ''}
                </Typography>
            </Box>

            {isSelected
                ? <CheckCircleIcon sx={{ fontSize: 18, color: '#0288d1', flexShrink: 0 }} />
                : <AddCircleOutlineIcon sx={{ fontSize: 18, color: '#cfd8dc', flexShrink: 0 }} />
            }
        </Box>
    );
}

function EditModal({ open, row, masterProduct, onClose, onSaved }) {
    const [selected, setSelected] = useState([]);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [modalPage, setModalPage] = useState(0);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (open && row) {
            setSelected(row.suggestedSkus || []);
            setSearch('');
            setDebouncedSearch('');
            setModalPage(0);
        }
    }, [open, row]);

    const handleSearch = e => {
        setSearch(e.target.value);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setDebouncedSearch(e.target.value);
            setModalPage(0);
        }, 280);
    };

    const toggle = sku => {
        setSelected(prev =>
            prev.includes(sku) ? prev.filter(x => x !== sku) : prev.length < 5 ? [...prev, sku] : prev
        );
    };

    const { data: filtered = [], isFetching } = useFilteredProducts(masterProduct, debouncedSearch, row?.sku_code);

    const paginated = filtered.slice(modalPage * MODAL_PAGE_SIZE, (modalPage + 1) * MODAL_PAGE_SIZE);

    const rowMaster = useMemo(
        () => (masterProduct || []).find(p => p.sku_code === row?.sku_code),
        [masterProduct, row?.sku_code]
    );

    const mutation = useMutation({
        mutationFn: updateSuggestionsApi,
        onSuccess: () => {
            Swal.fire({ icon: 'success', title: 'Saved!', text: 'Suggestions updated successfully.', timer: 2000, showConfirmButton: false });
            onSaved(row.id, selected);
            onClose();
        },
        onError: () => {
            Swal.fire({ icon: 'error', title: 'Failed', text: 'Could not update suggestions. Please try again.' });
        },
    });

    if (!row) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' } }}
        >
            <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #e0e6ed' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <AutoAwesomeIcon sx={{ fontSize: 18, color: '#0288d1' }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#04121f' }}>
                                Edit Suggestions
                            </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: '#607d8b' }}>
                            <span style={{ fontFamily: 'monospace', color: '#0288d1', fontWeight: 600 }}>{row.sku_code}</span>
                            {rowMaster?.product_desc && <span> · {rowMaster.product_desc}</span>}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ color: '#90a4ae' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 2, pb: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#90a4ae', display: 'block', mb: 1 }}>
                    Selected ({selected.length}/5)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, minHeight: 32, mb: 2 }}>
                    {selected.length === 0
                        ? <Typography variant="caption" sx={{ color: '#b0bec5', fontStyle: 'italic' }}>No suggestions added yet</Typography>
                        : selected.map(sku => (
                            <Chip key={sku} label={sku} size="small" onDelete={() => toggle(sku)}
                                sx={{ fontFamily: 'monospace', fontSize: 11, bgcolor: '#e3f2fd', color: '#0288d1', border: '1px solid #90caf9', '& .MuiChip-deleteIcon': { color: '#90caf9', '&:hover': { color: '#e53935' } } }}
                            />
                        ))
                    }
                </Box>

                {/* Max warning */}
                {selected.length >= 5 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff8e1', border: '1px solid #ffe082', borderRadius: '8px', px: 1.5, py: 1, mb: 2 }}>
                        <WarningAmberIcon sx={{ fontSize: 15, color: '#f59e0b' }} />
                        <Typography variant="caption" sx={{ color: '#b45309' }}>
                            Maximum 5 suggestions reached. Remove one to add another.
                        </Typography>
                    </Box>
                )}

                {/* Search */}
                <TextField
                    fullWidth size="small" placeholder="Search by SKU, name, brand or category…"
                    value={search} onChange={handleSearch}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: '#90a4ae' }} /></InputAdornment>,
                        endAdornment: isFetching ? <InputAdornment position="end"><CircularProgress size={14} sx={{ color: '#0288d1' }} /></InputAdornment> : null,
                    }}
                    sx={{ mb: 1.5, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0288d1' } }}
                />

                <Typography variant="caption" sx={{ color: '#90a4ae', display: 'block', mb: 1 }}>
                    {filtered.length.toLocaleString()} product{filtered.length !== 1 ? 's' : ''} found
                    {filtered.length > MODAL_PAGE_SIZE && ` · showing ${modalPage * MODAL_PAGE_SIZE + 1}–${Math.min((modalPage + 1) * MODAL_PAGE_SIZE, filtered.length)}`}
                </Typography>

                {/* Product list */}
                <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
                    {paginated.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4, color: '#b0bec5' }}>
                            <InventoryOutlinedIcon sx={{ fontSize: 36, opacity: 0.4, display: 'block', mx: 'auto', mb: 1 }} />
                            <Typography variant="caption">No products found</Typography>
                        </Box>
                    ) : paginated.map(p => (
                        <ProductItem
                            key={p.sku_code}
                            product={p}
                            isSelected={selected.includes(p.sku_code)}
                            isDisabled={!selected.includes(p.sku_code) && selected.length >= 5}
                            onToggle={toggle}
                        />
                    ))}
                </Box>

                {filtered.length > MODAL_PAGE_SIZE && (
                    <TablePagination
                        component="div" count={filtered.length} page={modalPage}
                        rowsPerPage={MODAL_PAGE_SIZE} rowsPerPageOptions={[MODAL_PAGE_SIZE]}
                        onPageChange={(_, newPage) => setModalPage(newPage)}
                        sx={{ borderTop: '1px solid #e0e6ed', mt: 1 }}
                    />
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e6ed', gap: 1 }}>
                <Button onClick={onClose} variant="outlined" size="small"
                    sx={{ textTransform: 'none', borderColor: '#cfd8dc', color: '#546e7a', '&:hover': { borderColor: '#90a4ae' } }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={() => mutation.mutate({ id: row.id, suggestedSkus: selected })}
                    disabled={mutation.isPending}
                    variant="contained" size="small"
                    startIcon={mutation.isPending ? <CircularProgress size={13} sx={{ color: 'white' }} /> : <CheckIcon sx={{ fontSize: 15 }} />}
                    sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#0288d1', '&:hover': { bgcolor: '#0277bd' }, '&:disabled': { bgcolor: '#b0bec5' } }}
                >
                    {mutation.isPending ? 'Saving…' : 'Save Suggestions'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* ─── Main Component ──────────────────────────────────────────────────── */

const SmartSuggestion = () => {
    const dispatch = useDispatch();
    const { masterProduct, productSuggestions, count, getStatus } = useSelector(s => s.product);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [editRow, setEditRow] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [overrides, setOverrides] = useState({});

    const debounceRef = useRef(null);

    useEffect(() => {
        dispatch(fetchProductSuggestions({ page: page + 1, limit: rowsPerPage, search }));
    }, [page, rowsPerPage, search, dispatch]);

    const handleSearch = e => {
        setSearchInput(e.target.value);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => { setSearch(e.target.value); setPage(0); }, 350);
    };

    const openEdit = row => { setEditRow(row); setModalOpen(true); };
    const closeEdit = () => { setModalOpen(false); setTimeout(() => setEditRow(null), 200); };

    const handleSaved = (id, skus) => setOverrides(prev => ({ ...prev, [id]: skus }));

    const tableData = (productSuggestions || []).map(row => ({
        ...row,
        suggestedSkus: overrides[row.id] !== undefined ? overrides[row.id] : (row.suggestedSkus || []),
    }));

    const getProduct = sku => (masterProduct || []).find(p => p.sku_code === sku);

    return (
        <MainLayout title="Smart Suggestion">
            <Box sx={{ p: 0 }}>

                <Box
                    sx={{
                        p: "12px 16px",
                        bgcolor: "background.paper",
                        background: "linear-gradient(to top left, #f5f8fa, #e9eef2)",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                        mb: 2,
                        border: "1px solid #e0e6ed",
                        borderTopLeftRadius: "12px",
                        borderTopRightRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                    }}
                >
                    <TextField
                        placeholder="Search..."
                        variant="outlined"
                        size="small"
                        value={searchInput}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: "20px", color: "#64748b" }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            backgroundColor: "white",
                            width: "320px",
                            "& .MuiOutlinedInput-root": {
                                height: "32px",
                                fontSize: "13px",
                            },
                        }}
                    />

                    {count != null && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: "#546e7a",
                                bgcolor: "white",
                                border: "1px solid #e0e6ed",
                                borderRadius: "20px",
                                px: 2,
                                py: "4px",
                                fontWeight: 600,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.8,
                            }}
                        >
                            <AutoAwesomeIcon sx={{ fontSize: 16, color: "#0288d1" }} />
                            {count.toLocaleString()} product{count !== 1 ? "s" : ""}
                        </Typography>
                    )}
                </Box>

                {/* Table */}
                <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <Table size="small">
                        <TableHead sx={{ background: '#04121f' }}>
                            <TableRow>
                                {['Sr. No', 'Product', 'SKU Code', 'Category', 'Brand', 'Suggested SKUs', 'Action'].map(h => (
                                    <TableCell key={h} align="left" sx={{ fontWeight: 'bold', color: 'white', whiteSpace: 'nowrap', py: 1.5 }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {getStatus === "pending" ? (
                                <TableBodySkeleton cols={7} rows={10} hasActions={true} />
                            ) : tableData.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 5, color: '#90a4ae' }}>
                                        <InventoryOutlinedIcon sx={{ fontSize: 36, display: 'block', mx: 'auto', mb: 1, opacity: 0.4 }} />
                                        No products found
                                    </TableCell>
                                </TableRow>
                            ) : tableData.map((item, index) => {
                                const d = getProduct(item.sku_code);
                                return (
                                    <TableRow key={item.id} hover sx={{ '&:hover': { bgcolor: '#f0f7ff' }, transition: 'background 0.15s' }}>
                                        <TableCell align="left" sx={{ color: '#78909c', fontSize: 12 }}>
                                            {page * rowsPerPage + index + 1}
                                        </TableCell>

                                        <TableCell align="left">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'left' }}>
                                                {d?.image ? (
                                                    <img src={d.image} alt={item.sku_code}
                                                        style={{ width: 38, height: 38, borderRadius: 6, objectFit: 'cover', border: '1px solid #e0e6ed', flexShrink: 0 }}
                                                        onError={e => { e.target.style.display = 'none'; }}
                                                    />
                                                ) : (
                                                    <Box sx={{ width: 38, height: 38, borderRadius: '6px', border: '1px solid #e0e6ed', bgcolor: '#f5f8fa', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                        <InventoryOutlinedIcon sx={{ fontSize: 16, color: '#cfd8dc' }} />
                                                    </Box>
                                                )}
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#263238', maxWidth: 160 }} noWrap>
                                                    {d?.product_desc || '—'}
                                                </Typography>
                                            </Box>
                                        </TableCell>

                                        <TableCell align="left">
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#0288d1', fontWeight: 600, bgcolor: '#e3f2fd', px: 1, py: 0.3, borderRadius: '4px' }}>
                                                <ClickableTableLink path="/admin/product" displayValue={item.sku_code} searchValue={item.sku_code} />
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="left">
                                            {d?.category
                                                ? <Chip label={d.category} size="small" sx={{ fontSize: 11, bgcolor: '#f3e8ff', color: '#7c3aed', border: '1px solid #ddd6fe', height: 22 }} />
                                                : <span style={{ color: '#b0bec5' }}>—</span>
                                            }
                                        </TableCell>

                                        <TableCell align="left">
                                            <Typography variant="caption" sx={{ color: '#546e7a' }}>{d?.brand || '—'}</Typography>
                                        </TableCell>

                                        <TableCell align="left">
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'left' }}>
                                                {item.suggestedSkus?.length > 0
                                                    ? item.suggestedSkus.map(s => (
                                                        <Chip key={s} label={s} size="small"
                                                            sx={{ fontFamily: 'monospace', fontSize: 10, bgcolor: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7', height: 20 }}
                                                        />
                                                    ))
                                                    : <Typography variant="caption" sx={{ color: '#b0bec5', fontStyle: 'italic' }}>No suggestions</Typography>
                                                }
                                            </Box>
                                        </TableCell>

                                        <TableCell align="left">
                                            <Tooltip title="Edit suggestions" arrow>
                                                <Button size="small" variant="outlined"
                                                    startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => openEdit(item)}
                                                    sx={{ textTransform: 'none', fontSize: 12, borderColor: '#90caf9', color: '#0288d1', py: 0.4, px: 1.5, '&:hover': { borderColor: '#0288d1', bgcolor: '#e3f2fd' } }}
                                                >
                                                    Edit
                                                </Button>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div" count={count || 0} page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 20, 50, 100, count || 500]}
                    sx={{ backgroundColor: "#ffffff" }}
                />
            </Box>

            <EditModal
                open={modalOpen} row={editRow} masterProduct={masterProduct}
                onClose={closeEdit} onSaved={handleSaved}
            />
        </MainLayout>
    );
};

export default SmartSuggestion;